import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/page-header";
import { FormError } from "@/components/form-error";
import { SubmitButton } from "@/components/submit-button";
import { useCreateApiKeyMutation, useUpdateApiKeyMutation } from "@/hooks/use-api-keys";
import { useServicesQuery } from "@/hooks/use-services";
import { apiKeySchema, type ApiKeyFormData } from "@/lib/validations/api-key";
import type { ApiKey } from "@/types/api-key";

const ALL_SERVICES_VALUE = "__all__";

interface ApiKeyFormProps {
  mode: "create" | "edit";
  apiKey?: ApiKey;
}

export function ApiKeyForm({ mode, apiKey }: ApiKeyFormProps) {
  const navigate = useNavigate();
  const createMutation = useCreateApiKeyMutation();
  const updateMutation = useUpdateApiKeyMutation();
  const { data: services } = useServicesQuery();

  const defaultValues =
    mode === "edit" && apiKey
      ? { name: apiKey.name, service_id: apiKey.service_id }
      : { name: "", service_id: null };

  const form = useForm<ApiKeyFormData>({
    resolver: zodResolver(apiKeySchema),
    defaultValues,
  });

  useEffect(() => {
    if (mode === "edit" && apiKey) {
      form.reset({ name: apiKey.name, service_id: apiKey.service_id });
    }
  }, [apiKey, form, mode]);

  const saveMutation = mode === "create" ? createMutation : updateMutation;

  const clearApiError = () => {
    if (saveMutation.isError) {
      saveMutation.reset();
    }
  };

  const onSubmit = async (data: ApiKeyFormData) => {
    saveMutation.reset();
    try {
      if (mode === "create") {
        const result = await createMutation.mutateAsync({
          name: data.name,
          service_id: data.service_id,
        });
        navigate(`/settings/api-keys/${result.data.id}`);
      } else if (apiKey) {
        await updateMutation.mutateAsync({
          id: apiKey.id,
          data: { name: data.name, service_id: data.service_id },
        });
        navigate(`/settings/api-keys/${apiKey.id}`);
      }
    } catch {
      // Error is handled by mutation state
    }
  };

  const isCreate = mode === "create";
  const pageTitle = isCreate ? "Create API Key" : "Edit API Key";
  const saveButtonText = isCreate ? "Create API Key" : "Save changes";
  const savingButtonText = isCreate ? "Creating..." : "Saving...";

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: "API Keys", to: "/settings/api-keys" },
          ...(apiKey
            ? [{ label: apiKey.name, to: `/settings/api-keys/${apiKey.id}` }]
            : []),
        ]}
        title={pageTitle}
        actions={
          <SubmitButton
            form="api-key-form"
            pending={form.formState.isSubmitting}
            pendingText={savingButtonText}
          >
            {saveButtonText}
          </SubmitButton>
        }
      />

      <Card className="mx-auto w-full max-w-3xl">
        <CardHeader>
          <CardTitle>API Key Details</CardTitle>
          <CardDescription>
            Configure your API key.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            id="api-key-form"
            onSubmit={form.handleSubmit(onSubmit)}
            onChange={clearApiError}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="My API Key"
                maxLength={255}
                {...form.register("name")}
                aria-invalid={!!form.formState.errors.name}
                aria-describedby={
                  form.formState.errors.name ? "name-error" : undefined
                }
              />
              {form.formState.errors.name && (
                <p
                  id="name-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_id">Service</Label>
              <Select
                value={form.watch("service_id") ?? ALL_SERVICES_VALUE}
                onValueChange={(value) =>
                  form.setValue(
                    "service_id",
                    value === ALL_SERVICES_VALUE ? null : value
                  )
                }
              >
                <SelectTrigger id="service_id" className="cursor-pointer">
                  <SelectValue placeholder="All services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SERVICES_VALUE}>
                    All services
                  </SelectItem>
                  {services?.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.title || service.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {saveMutation.isError && (
              <FormError>
                <p>
                  {saveMutation.error instanceof AxiosError &&
                  saveMutation.error.response?.data?.error?.message
                    ? saveMutation.error.response.data.error.message
                    : `Failed to ${isCreate ? "create" : "update"} API key. Please try again.`}
                </p>
              </FormError>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
